import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ClaimTheme } from '../constants';

interface AnimatedButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  type?: 'button' | 'submit';
  icon?: React.ReactNode;
  theme?: ClaimTheme;
}

// Theme-specific variants for primary button
const primaryVariants = {
  invol: 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:from-blue-600 hover:to-blue-700',
  drboese: 'bg-gradient-to-r from-[rgb(255,211,52)] to-amber-400 text-black shadow-lg shadow-yellow-500/25 hover:shadow-yellow-500/40 hover:from-amber-400 hover:to-amber-500',
};

const baseVariants = {
  secondary:
    'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300',
  ghost: 'text-slate-600 hover:text-slate-900 hover:bg-slate-100',
  danger:
    'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg shadow-red-500/25 hover:shadow-red-500/40',
};

const sizes = {
  sm: 'px-3 py-1.5 text-sm rounded-lg',
  md: 'px-4 py-2 text-sm rounded-xl',
  lg: 'px-6 py-3 text-base rounded-xl',
};

export function AnimatedButton({
  children,
  onClick,
  disabled = false,
  loading = false,
  variant = 'primary',
  size = 'md',
  className,
  type = 'button',
  icon,
  theme = 'invol',
}: AnimatedButtonProps) {
  const isDisabled = disabled || loading;

  // Get the variant styles - primary is theme-aware, others are not
  const getVariantStyles = () => {
    if (variant === 'primary') {
      return primaryVariants[theme];
    }
    return baseVariants[variant];
  };

  // Theme-aware focus ring
  const focusRing = theme === 'drboese'
    ? 'focus-visible:ring-yellow-500'
    : 'focus-visible:ring-blue-500';

  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={isDisabled}
      className={cn(
        'relative inline-flex items-center justify-center gap-2 font-medium transition-all',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        focusRing,
        getVariantStyles(),
        sizes[size],
        isDisabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      whileHover={isDisabled ? {} : { scale: 1.02 }}
      whileTap={isDisabled ? {} : { scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
    >
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading...</span>
        </>
      ) : (
        <>
          {icon}
          {children}
        </>
      )}
    </motion.button>
  );
}
